/**
 * HR payroll API client (Phase 5). Module-level typed async fns over the `api`
 * singleton. Precedent: lib/hr-attendance-api.ts. Every endpoint is
 * house-enveloped ({success, data, message?}) — unwrap `response.data.data`.
 *
 * ALL endpoints require `hr.payroll.manage` AND the `hr_payroll_export_enabled`
 * feature flag (enforced server-side). `api` already prefixes /api/v1.
 */

import api from "./api"
import { buildQueryParams } from "./utils/query-params"
import type { ApiResponse } from "@/types"
import type {
  PayrollRun,
  PayrollRunDetail,
  CreatePayrollRunData,
  PayrollAdjustment,
  CreatePayrollAdjustmentData,
  PayRate,
  CreatePayRateData,
  CommissionTier,
  SetCommissionTiersData,
  MyCommission,
  CommissionBreakdown,
} from "./hr-types"

// ── Payroll runs ─────────────────────────────────────────────────────────────

/** GET /hr/payroll-runs?year= — the run register for one calendar year. */
export const getPayrollRuns = async (year: number): Promise<PayrollRun[]> => {
  const query = buildQueryParams({ year })
  const response = await api.get<ApiResponse<PayrollRun[]>>(
    `/hr/payroll-runs?${query}`,
  )
  return response.data.data
}

/**
 * POST /hr/payroll-runs — open a draft run for a (year, month). Returns 422
 * {message} when a run already exists for that period.
 */
export const createPayrollRun = async (
  data: CreatePayrollRunData,
): Promise<PayrollRun> => {
  const response = await api.post<ApiResponse<PayrollRun>>(
    `/hr/payroll-runs`,
    data,
  )
  return response.data.data
}

/** GET /hr/payroll-runs/{id} — the run summary + its per-staff register lines. */
export const getPayrollRun = async (id: string): Promise<PayrollRunDetail> => {
  const response = await api.get<ApiResponse<PayrollRunDetail>>(
    `/hr/payroll-runs/${id}`,
  )
  return response.data.data
}

/**
 * POST /hr/payroll-runs/{id}/recompute — refresh the register from current
 * pay-rates/attendance/adjustments. Returns 422 {message} when finalized.
 */
export const recomputePayrollRun = async (
  id: string,
): Promise<PayrollRunDetail> => {
  const response = await api.post<ApiResponse<PayrollRunDetail>>(
    `/hr/payroll-runs/${id}/recompute`,
  )
  return response.data.data
}

/**
 * POST /hr/payroll-runs/{id}/finalize — freeze the run (irreversible). Returns
 * the finalized summary.
 */
export const finalizePayrollRun = async (id: string): Promise<PayrollRun> => {
  const response = await api.post<ApiResponse<PayrollRun>>(
    `/hr/payroll-runs/${id}/finalize`,
  )
  return response.data.data
}

// ── Adjustments ───────────────────────────────────────────────────────────────

/** GET /hr/payroll-adjustments?year&month — the period's manual adjustments. */
export const getPayrollAdjustments = async (
  year: number,
  month: number,
): Promise<PayrollAdjustment[]> => {
  const query = buildQueryParams({ year, month })
  const response = await api.get<ApiResponse<PayrollAdjustment[]>>(
    `/hr/payroll-adjustments?${query}`,
  )
  return response.data.data
}

/**
 * POST /hr/payroll-adjustments — file a bonus/deduction for a staff member in a
 * period. Returns 422 {message} when the covering run is finalized.
 */
export const createPayrollAdjustment = async (
  data: CreatePayrollAdjustmentData,
): Promise<PayrollAdjustment> => {
  const response = await api.post<ApiResponse<PayrollAdjustment>>(
    `/hr/payroll-adjustments`,
    data,
  )
  return response.data.data
}

/**
 * DELETE /hr/payroll-adjustments/{id} — remove an adjustment. Returns 422
 * {message} when the covering run is finalized.
 */
export const deletePayrollAdjustment = async (id: string): Promise<void> => {
  await api.delete(`/hr/payroll-adjustments/${id}`)
}

// ── Pay rates ─────────────────────────────────────────────────────────────────

/**
 * GET /hr/pay-rates?user_id= — a staff member's dated pay-rate history
 * (newest first).
 */
export const getPayRates = async (userId: number): Promise<PayRate[]> => {
  const query = buildQueryParams({ user_id: userId })
  const response = await api.get<ApiResponse<PayRate[]>>(
    `/hr/pay-rates?${query}`,
  )
  return response.data.data
}

/**
 * POST /hr/pay-rates — add a new dated rate. Returns 422 {message} on a
 * duplicate `from_date`.
 */
export const createPayRate = async (
  data: CreatePayRateData,
): Promise<PayRate> => {
  const response = await api.post<ApiResponse<PayRate>>(`/hr/pay-rates`, data)
  return response.data.data
}

/**
 * DELETE /hr/pay-rates/{id} — remove the NEWEST rate only. Returns 422
 * {message} otherwise or when a finalized run already covers it.
 */
export const deletePayRate = async (id: string): Promise<void> => {
  await api.delete(`/hr/pay-rates/${id}`)
}

// ── Agent commissions ─────────────────────────────────────────────────────────
//
// All three endpoints additionally require the `agent_commissions_enabled`
// feature flag (404 when off). The tier endpoints sit inside the payroll group
// (`hr.payroll.manage`); `commissions/me` only needs `commissions.view.own`.

/** GET /hr/commission-tiers — the clinic's global marginal ladder, ordered. */
export const getCommissionTiers = async (): Promise<CommissionTier[]> => {
  const response = await api.get<ApiResponse<CommissionTier[]>>(
    `/hr/commission-tiers`,
  )
  return response.data.data
}

/**
 * PUT /hr/commission-tiers — replace the WHOLE ladder atomically. Returns 422
 * {message} when the bands are not contiguous from 0 or a non-last band has a
 * null max. Edits only affect FUTURE draft recomputes (finalized runs freeze).
 */
export const setCommissionTiers = async (
  data: SetCommissionTiersData,
): Promise<CommissionTier[]> => {
  const response = await api.put<ApiResponse<CommissionTier[]>>(
    `/hr/commission-tiers`,
    data,
  )
  return response.data.data
}

/**
 * GET /hr/commissions/me — the caller's own running commission estimate for the
 * current month. Returns 200 {participating:false, …} when not opted in.
 */
export const getMyCommission = async (): Promise<MyCommission> => {
  const response = await api.get<ApiResponse<MyCommission>>(
    `/hr/commissions/me`,
  )
  return response.data.data
}

/**
 * GET /hr/commission-breakdown/{user}?year=&month= — the payroll-register row
 * drill-down: bands plus every contributing payment for that user's month.
 * Same gates as the register (hr.payroll.manage + the commissions flag).
 */
export const getCommissionBreakdown = async (
  userId: number,
  year: number,
  month: number,
): Promise<CommissionBreakdown> => {
  const response = await api.get<ApiResponse<CommissionBreakdown>>(
    `/hr/commission-breakdown/${userId}?year=${year}&month=${month}`,
  )
  return response.data.data
}

/** EKDent 260907 — one settled line behind a doctor's payroll commission. */
export interface DoctorCommissionBreakdownLine {
  id: string
  earned_on: string | null
  service: string | null
  invoice_id: string
  invoice_number: string | null
  patient: string | null
  payment_method: string | null
  base_amount: number
  /** The rate FROZEN when the money landed — not whatever is configured now. */
  rate_type: "percent" | "fixed" | null
  rate_percent: number | null
  rate_amount: number | null
  rate_source: "item" | "category" | "doctor_default" | null
  amount: number
}

export interface DoctorCommissionBreakdown {
  user_id: number
  year: number
  month: number
  currency: string | null
  total: number
  collected: number
  lines: DoctorCommissionBreakdownLine[]
}

export const getDoctorCommissionBreakdown = async (
  userId: number,
  year: number,
  month: number,
): Promise<DoctorCommissionBreakdown> => {
  const response = await api.get<ApiResponse<DoctorCommissionBreakdown>>(
    `/hr/doctor-commission-breakdown/${userId}?year=${year}&month=${month}`,
  )
  return response.data.data
}

/** EKDent 260907 — one band of the month's bonus ladder. */
export interface BonusTier {
  id: string
  order: number
  min_percentage: number
  max_percentage: number | null
  bonus_amount: number
  /** The band this person actually landed in. */
  achieved: boolean
}

export interface BonusBreakdown {
  user_id: number
  /** Null when no target was set for the month — distinct from missing it. */
  period: { metric: string; target_value: number } | null
  snapshot: {
    current_value: number
    target_value: number
    percentage: number
    bonus_earned: number
    tier_achieved_id: string | null
    last_calculated_at: string | null
  } | null
  tiers: BonusTier[]
}

export const getBonusBreakdown = async (
  userId: number,
  year: number,
  month: number,
): Promise<BonusBreakdown> => {
  const response = await api.get<ApiResponse<BonusBreakdown>>(
    `/hr/bonus-breakdown/${userId}?year=${year}&month=${month}`,
  )
  return response.data.data
}
